/**
 * The pure decision behind "may this user be shown a purchase surface?" on the
 * Subscribe page (#4189), extracted so it can be unit-tested without a page
 * harness — the same shape as `pricing-failure-policy.ts` and
 * `subscribe-store-availability.ts` next door.
 *
 * This is worth pinning because it is a money path with an expensive failure
 * mode. The page has five purchase surfaces (the plan picker, both web checkout
 * cards, the native IAP card, and the "already have a subscription" card). They
 * previously each carried their own hand-written subset of these guards, in
 * different orders, and two omitted `crossPlatformSubscription` entirely.
 *
 * That class of drift already shipped once here: hiding the plan picker alone
 * left a live, card-on-file checkout mounted underneath a PAUSED member, so
 * completing it charged them twice AND the webhook's duplicate-subscription
 * guard then cancelled the paused subscription — losing both. One answer, used
 * by every surface, is what stops a newly added surface from getting it wrong.
 */

/** The independent reasons a purchase surface must stay hidden. */
export interface PurchaseOfferState {
  /** the membership is PAUSED: the right action is Resume, not a second purchase */
  pausedMembership: boolean;
  /** the subscription is billed on another platform, so buying here would double up */
  crossPlatformSubscription: boolean;
  /** a checkout is already in flight; a second one could charge twice */
  previousCheckoutWaiting: boolean;
  /** prices are not resolved yet, so any price shown would be wrong or blank */
  loadingPrices: boolean;
  /**
   * the person is signed in, but the server's record of their subscription has
   * not been loaded yet. Every other veto above is worked out from that record,
   * so until it arrives none of them can be trusted: a warm restart shows this
   * page before its server login, and a paying subscriber would otherwise be
   * offered a second subscription (df-6cbfc9ac5a80)
   */
  subscriptionRecordUnknown: boolean;
  /**
   * the record arrived after the page opened and shows a subscription the person
   * can cancel or resume. The page is leaving for their default page, and until
   * that navigation lands nothing here may be sold (df-6cbfc9ac5a80)
   */
  alreadySubscribed: boolean;
}

/**
 * Whether it is safe to show ANY purchase surface.
 *
 * Every condition is a veto: the answer is yes only when every one is false.
 * Written as an explicit conjunction rather than a loop over the object's values
 * so that adding a field to `PurchaseOfferState` without deciding how it affects
 * the gate is a compile error rather than a silent pass.
 */
export function canOfferPurchase(state: PurchaseOfferState): boolean {
  return !state.pausedMembership
    && !state.crossPlatformSubscription
    && !state.previousCheckoutWaiting
    && !state.loadingPrices
    && !state.subscriptionRecordUnknown
    && !state.alreadySubscribed;
}
